import {
  Component, Input, forwardRef,
  HostListener, ElementRef,
  signal,
  Output,
  EventEmitter
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';

@Component({
  selector: 'app-datetime-picker-input',
  standalone: true,
  imports: [
    CommonModule, MatFormFieldModule, MatInputModule,
    MatIconModule, MatButtonModule, MatDatepickerModule
  ],
  providers: [
    provideNativeDateAdapter(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerInputComponent),
      multi: true
    }
  ],
  templateUrl: './datetime-picker-input.component.html',
  styleUrl:    './datetime-picker-input.component.scss'
})
export class DatetimePickerInputComponent implements ControlValueAccessor {

  @Input() label = 'Date & Time';
  @Input() use24Hour    = true;
  meridian: 'AM' | 'PM' = 'AM';
  private readonly FIXED_POSITIONS = new Set([2, 5, 10, 13]);
  inputValue = signal<string>('');
  panelOpen   = false;
  isDisabled  = false;
  @Input() panelPosition: 'left' | 'right' = 'left';
  @Output() selectedDate = new EventEmitter<Date | null>();
  internalDate: Date | null = null;
  hours    = 0;
  minutes  = 0;
  private get fixedPos(): Set<number> {
    return this.use24Hour
        ? new Set([2, 5, 10, 13]) 
        : new Set([2, 5, 10, 13, 16]);
}
private get formatLength(): number { return this.use24Hour ? 16 : 19; }

  private onChange  = (_: any) => {};
  private onTouched = ()       => {};

  constructor(private elRef: ElementRef) {}

  // ── display ────────────────────────────────────────────────────────────
  get paddedHour():   string { return String(this.hours).padStart(2, '0'); }
  get paddedMinute(): string { return String(this.minutes).padStart(2, '0'); }

  get displayValue(): string {
    if (!this.internalDate) return '';
    const d  = this.internalDate;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const base = `${mm}/${dd}/${d.getFullYear()} ${this.paddedHour}:${this.paddedMinute}`;
    return this.use24Hour ? base : `${base} ${this.meridian}`;
  }

  private toFullHours(): number {
    if (this.use24Hour) return this.hours;
    let h = this.hours;
    if (this.meridian === 'PM' && h !== 12) h += 12;
    if (this.meridian === 'AM' && h === 12) h = 0;
    return h;
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────
  writeValue(value: any): void {
    if (!value) {
        this.internalDate = null;
        this.inputValue.set('');
        this.syncTimeFromDate(new Date())
        return;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return;
    this.internalDate = new Date(date);
    this.syncTimeFromDate(date);
    this.updateDisplay();
  }

  private updateDisplay(): void {
    if (!this.internalDate) { this.inputValue.set(''); return; }
    const d  = this.internalDate;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const base = `${mm}/${dd}/${d.getFullYear()} ${this.paddedHour}:${this.paddedMinute}`;
    this.inputValue.set(this.use24Hour ? base : `${base} ${this.meridian}`);
  }

  registerOnChange(fn: any):    void { this.onChange  = fn; }
  registerOnTouched(fn: any):   void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.isDisabled = d; }

  // ── panel ───────────────────────────────────────────────────────────────
  togglePanel(e: MouseEvent): void {
    if (this.isDisabled) return;
    e.stopPropagation();
    this.panelOpen = !this.panelOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(e.target))
      this.panelOpen = false;
  }

  // ── calendar ────────────────────────────────────────────────────────────
  onCalendarDateSelected(date: Date): void {
    this.internalDate = new Date(date);
    this.updateDateTimeValue();
  }
  private updateDateTimeValue(): void {
    if (!this.internalDate) return;

    const result = new Date(this.internalDate);
    result.setHours(this.toFullHours(), this.minutes, 0, 0);

    this.internalDate = result;
    this.updateDisplay();
    this.onChange(result);
    this.onTouched();
    this.selectedDate.emit(result);
    
  }
  // ── spinners ────────────────────────────────────────────────────────────
  incrementHour(): void {
    this.hours = this.use24Hour ? (this.hours >= 23 ? 0  : this.hours + 1) : (this.hours >= 12 ? 1  : this.hours + 1);
    this.updateDateTimeValue();
  }
  decrementHour(): void {
    this.hours = this.use24Hour ? (this.hours <= 0  ? 23 : this.hours - 1) : (this.hours <= 1  ? 12 : this.hours - 1);
    this.updateDateTimeValue();
  }
  incrementMinute(): void { this.minutes = this.minutes >= 59 ? 0 : this.minutes + 1; this.updateDateTimeValue(); }
  decrementMinute(): void { this.minutes = this.minutes <= 0 ? 59 : this.minutes - 1; this.updateDateTimeValue(); }
  toggleMeridian(): void { this.meridian = this.meridian === 'AM' ? 'PM' : 'AM'; this.updateDateTimeValue(); }

  private syncTimeFromDate(date: Date): void {
    const h = date.getHours();
    this.minutes = date.getMinutes();
    if (this.use24Hour) {
      this.hours = h;
    } else {
      this.meridian = h >= 12 ? 'PM' : 'AM';
      this.hours    = h % 12 === 0 ? 12 : h % 12;
    }
  }

  onManualInput(event: Event): void {
    // Allow user to type freely — don't block input
    const val = (event.target as HTMLInputElement).value;
    this.inputValue.set(val);

    // Try to parse as user types — only update internally when valid
    const parsed = this.parseManualDate(val);
    if (parsed && !isNaN(parsed.getTime())) {
      this.internalDate = new Date(parsed);
      this.syncTimeFromDate(parsed);
      this.onChange(parsed);
      this.onTouched();
    }
  }

  onManualBlur(event: Event): void {
    const val = (event.target as HTMLInputElement).value.trim();

    if (!val) {
      if (this.internalDate) {
        this.updateDisplay();
      } else {
        this.inputValue.set('');
        this.onChange(null);
      }
      return;
    }

    const parsed = this.parseManualDate(val);
    if (parsed && !isNaN(parsed.getTime())) {
      this.internalDate = new Date(parsed);
      this.syncTimeFromDate(parsed);
      this.onChange(parsed);
      this.onTouched();
      this.updateDisplay();
      this.selectedDate.emit(parsed);
    } else {
      this.updateDisplay();
    }
  }

  confirmSelection(): void {
    if (!this.internalDate) return;
    const result = new Date(this.internalDate);

    let finalHour = this.hours;

    if (!this.use24Hour) {
      if (this.meridian === 'PM' && finalHour !== 12) {
        finalHour += 12;
      }

      if (this.meridian === 'AM' && finalHour === 12) {
        finalHour = 0;
      }
    }

    result.setHours(finalHour, this.minutes, 0, 0);
    this.internalDate = new Date(result);
    //this.syncTimeFromDate(result);
    this.updateDisplay();
    this.onChange(result);
    this.onTouched();
    this.panelOpen = false;
  }

  private parseManualDate(val: string): Date | null {
    if (this.use24Hour) {
      const m = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
      if (m) {
        const [, mm, dd, yyyy, hh, min] = m;
        const h = parseInt(hh), mn = parseInt(min);
        if (h > 23 || mn > 59) return null;
        const d = new Date(+yyyy, +mm - 1, +dd, h, mn);
        return isNaN(d.getTime()) ? null : d;
      }
    } else {
      const m = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (m) {
        const [, mm, dd, yyyy, hh, min, mer] = m;
        let h = parseInt(hh);
        const mn = parseInt(min);
        if (mer.toUpperCase() === 'PM' && h !== 12) h += 12;
        if (mer.toUpperCase() === 'AM' && h === 12) h = 0;
        const d = new Date(+yyyy, +mm - 1, +dd, h, mn);
        return isNaN(d.getTime()) ? null : d;
      }
    }
    const dateOnly = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateOnly) {
      const [, mm, dd, yyyy] = dateOnly;
      const e = this.internalDate ?? new Date();
      const d = new Date(+yyyy, +mm - 1, +dd, e.getHours(), e.getMinutes());
      return isNaN(d.getTime()) ? null : d;
    }
    const fb = new Date(val);
    return isNaN(fb.getTime()) ? null : fb;
  }

  onKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const key   = event.key;

    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown', 'Home','End','Tab','Enter','Escape',
      'Backspace','Delete'].includes(key)) return;

    const isAP = !this.use24Hour && /^[AaPp]$/.test(key);
    if (/^[a-zA-Z]$/.test(key) && !isAP) { event.preventDefault(); return; }
    if (!(/^\d$/.test(key)) && !isAP && !(/^[\/: ]$/.test(key))) {
        event.preventDefault(); return;
    }

    const fmtLen  = this.formatLength;
    const fixedPos = this.fixedPos;

    if (/^\d$/.test(key)) {
      event.preventDefault();
      const pos = input.selectionStart ?? 0;
      const val = input.value.padEnd(fmtLen, '_');
      let targetPos = pos;
      while (fixedPos.has(targetPos) && targetPos < fmtLen - 1) targetPos++;
      if (targetPos >= fmtLen) return;
      const newVal = val.substring(0, targetPos) + key + val.substring(targetPos + 1);
      this.inputValue.set(newVal);
      let nextPos = targetPos + 1;
      while (fixedPos.has(nextPos) && nextPos < fmtLen) nextPos++;
      setTimeout(() => input.setSelectionRange(nextPos, nextPos), 0);
      const parsed = this.parseManualDate(newVal);
      if (parsed && !isNaN(parsed.getTime())) {
        this.internalDate = parsed;
        this.syncTimeFromDate(parsed);
        this.onChange(parsed);
      }
    }
    
    if (isAP) {
      event.preventDefault();
      const val = input.value;
      if (val.length >= 19) {
        const newMer = /^[Aa]$/.test(key) ? 'AM' : 'PM';
        const newVal = val.substring(0, 17) + newMer;
        this.inputValue.set(newVal);
        this.meridian = newMer as 'AM' | 'PM';
        setTimeout(() => input.setSelectionRange(17, 19), 0);
        if (this.internalDate) {
          const result = new Date(this.internalDate);
          result.setHours(this.toFullHours(), this.minutes, 0, 0);
          this.internalDate = result;
          this.onChange(result);
        }
      }
    }
  }
}
